import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Printer, Download, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ordersAPI, csvAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { printContent, generateOrdersListHTML, generateStockReportHTML, generateDeliveryReportHTML } from '../lib/print';
import api from '../lib/api';
import { generateGoogleMapsLink } from '../components/MapView';

import { OrderSelectionBar } from '../components/admin/orders/OrderSelectionBar';
import { DeliveryMapSection } from '../components/admin/orders/DeliveryMapSection';
import { OrderFilters } from '../components/admin/orders/OrderFilters';
import { OrdersTable } from '../components/admin/orders/OrdersTable';
import { EmailPreparationModal } from '../components/admin/orders/EmailPreparationModal';

const ORDERS_PER_PAGE = 50;

const downloadOrderPdf = async (orderId, orderNumber) => {
    try {
        const res = await ordersAPI.downloadPdf(orderId);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `BonDeCommande_${orderNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
    } catch {
        toast.error('Erreur lors du téléchargement du PDF');
    }
};

export const AdminOrders = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.is_super_admin === true;
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalOrders, setTotalOrders] = useState(0);
    const [selectedOrders, setSelectedOrders] = useState(new Set());
    const [reportLoading, setReportLoading] = useState(false);
    const [deliveryStops, setDeliveryStops] = useState([]);
    const [showMap, setShowMap] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipient, setEmailRecipient] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [pendingStockData, setPendingStockData] = useState(null);

    useEffect(() => {
        loadOrders();
        api.put('/admin/orders/mark-seen').catch(() => {});
    }, [currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(search);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const loadOrders = async () => {
        try {
            const skip = (currentPage - 1) * ORDERS_PER_PAGE;
            const params = { skip, limit: ORDERS_PER_PAGE };
            if (searchQuery) params.search = searchQuery;
            if (statusFilter !== 'all') params.status = statusFilter;
            const res = await ordersAPI.getAll(params);
            setOrders(res.data.orders);
            setTotalOrders(res.data.total || 0);
        } catch (error) {
            console.error('Error loading orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await ordersAPI.updateStatus(orderId, newStatus);
            toast.success('Statut mis à jour - Email de notification envoyé');
            loadOrders();
        } catch {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleExportCSV = async () => {
        toast.info('Préparation du téléchargement...');
        const token = localStorage.getItem('prixdeal_token');
        try {
            const result = await csvAPI.downloadOrders(token);
            if (result.success) {
                toast.success('Fichier téléchargé ! Vérifiez votre dossier Téléchargements.');
            } else {
                toast.error(`Erreur: ${result.error || 'Téléchargement échoué'}`);
            }
        } catch {
            toast.error('Erreur lors du téléchargement. Veuillez réessayer.');
        }
    };

    const handlePrintList = () => {
        const filters = {};
        if (statusFilter !== 'all') filters.status = statusFilter;
        if (search) filters.search = search;
        const html = generateOrdersListHTML(orders, filters);
        const success = printContent('Liste des Commandes', html);
        if (success) toast.success('Fenêtre d\'impression ouverte');
        else toast.error('La fenêtre d\'impression a été bloquée. Autorisez les popups dans Edge.');
    };

    const toggleOrder = (orderId) => {
        setSelectedOrders(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedOrders.size === orders.length) setSelectedOrders(new Set());
        else setSelectedOrders(new Set(orders.map(o => o.id)));
    };

    const handleReport = async (type) => {
        if (selectedOrders.size === 0) { toast.error('Selectionnez au moins une commande'); return; }
        setReportLoading(true);
        try {
            const res = await api.post('/admin/orders/reports', { order_ids: Array.from(selectedOrders) });
            const data = res.data;
            if (type === 'stock') {
                printContent('Preparation de Commande', generateStockReportHTML(data.stock_report, data.orders_count));
                toast.success('Preparation de commande ouverte');
            } else if (type === 'stock_pdf') {
                const pdfRes = await api.post('/admin/orders/preparation-pdf', { stock_items: data.stock_report, orders_count: data.orders_count }, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
                const a = document.createElement('a'); a.href = url; a.download = `preparation_commande_${new Date().toISOString().slice(0, 10)}.pdf`; a.click();
                window.URL.revokeObjectURL(url);
                toast.success('PDF telecharge');
            } else if (type === 'stock_email') {
                setPendingStockData({ stock_items: data.stock_report, orders_count: data.orders_count });
                setEmailRecipient('');
                setShowEmailModal(true);
            } else {
                printContent('Rapport Tournee Livraison', generateDeliveryReportHTML(data.delivery_report, data.orders_count));
                toast.success('Rapport livraison ouvert');
            }
        } catch {
            toast.error('Erreur lors de la generation du rapport');
        } finally {
            setReportLoading(false);
        }
    };

    const handleSendEmail = async () => {
        if (!emailRecipient.trim()) { toast.error('Veuillez saisir une adresse email'); return; }
        setEmailSending(true);
        try {
            await api.post('/admin/orders/send-preparation-email', { recipient_email: emailRecipient.trim(), ...pendingStockData });
            toast.success(`Email envoye a ${emailRecipient}`);
            setShowEmailModal(false);
            setEmailRecipient('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setEmailSending(false);
        }
    };

    const handleShowMap = async () => {
        if (selectedOrders.size === 0) return;
        try {
            const res = await api.post('/admin/orders/delivery-map', { order_ids: Array.from(selectedOrders) });
            setDeliveryStops(res.data.stops.filter(s => s.lat && s.lng).map(s => ({
                ...s, id: s.order_id, label: `${s.order_number} - ${s.company_name}`,
                extra: `Total: ${s.total ? s.total.toFixed(2) + ' EUR' : ''}`, type: 'delivery',
            })));
            setShowMap(true);
        } catch {
            toast.error('Erreur chargement carte');
        }
    };

    const copyMapsLink = () => {
        const link = generateGoogleMapsLink(deliveryStops.filter(s => s.address));
        if (link) { navigator.clipboard.writeText(link); toast.success('Lien Google Maps copie !'); }
    };

    const handleBulkDelete = async () => {
        if (selectedOrders.size === 0) {
            toast.error('Aucune commande selectionnee');
            return;
        }
        if (!window.confirm(`Etes-vous sur de vouloir supprimer ${selectedOrders.size} commande(s) ? Cette action est irreversible.`)) {
            return;
        }
        setBulkDeleting(true);
        try {
            const res = await ordersAPI.bulkDelete({ ids: Array.from(selectedOrders) });
            toast.success(res.data.message || `${selectedOrders.size} commande(s) supprimee(s)`);
            setSelectedOrders(new Set());
            await loadOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };
    const handleBulkDeleteAll = async () => {
        if (!window.confirm('SUPPRIMER TOUTES LES COMMANDES ? Cette action est irreversible.')) {
            return;
        }
        setBulkDeleting(true);
        try {
            const res = await ordersAPI.bulkDelete({ all: true });
            toast.success(res.data.message || 'Toutes les commandes supprimees');
            setSelectedOrders(new Set());
            await loadOrders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };

    const totalPages = Math.ceil(totalOrders / ORDERS_PER_PAGE);

    if (loading) {
        return (
            <div className="min-h-screen bg-muted/50 flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-muted/50" data-testid="admin-orders-page">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                        <h1 className="font-heading text-3xl font-bold tracking-tight">Gestion des commandes</h1>
                        <div className="flex flex-wrap gap-2">
                            <Button asChild className="bg-leaf hover:bg-leaf/90 text-white rounded-xl" data-testid="create-order-btn">
                                <Link to="/admin/commandes/creer"><Plus className="h-4 w-4 mr-2" />Saisir une commande</Link>
                            </Button>
                            <Button onClick={handlePrintList} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="print-orders-button">
                                <Printer className="h-4 w-4 mr-2" />Imprimer
                            </Button>
                            <Button onClick={handleExportCSV} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="export-orders-button">
                                <Download className="h-4 w-4 mr-2" />Exporter CSV
                            </Button>
                        </div>
                    </div>

                    <OrderSelectionBar
                        selectedCount={selectedOrders.size}
                        reportLoading={reportLoading}
                        onReport={handleReport}
                        onShowMap={handleShowMap}
                        onDeselectAll={() => setSelectedOrders(new Set())}
                    />

                    {isSuperAdmin && (
                        <div className="flex items-center justify-between gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="text-sm text-red-700">
                                {selectedOrders.size > 0
                                    ? `${selectedOrders.size} commande(s) selectionnee(s)`
                                    : 'Selectionnez des commandes pour les supprimer en lot'}
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleBulkDelete}
                                    disabled={selectedOrders.size === 0 || bulkDeleting}
                                    className="border-red-300 text-red-700 hover:bg-red-100"
                                    data-testid="bulk-delete-orders"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Supprimer la selection
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleBulkDeleteAll}
                                    disabled={bulkDeleting || orders.length === 0}
                                    data-testid="bulk-delete-all-orders"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Tout supprimer
                                </Button>
                            </div>
                        </div>
                    )}

                    {showMap && <DeliveryMapSection stops={deliveryStops} onCopyLink={copyMapsLink} onClose={() => setShowMap(false)} />}

                    <OrderFilters
                        search={search}
                        onSearchChange={setSearch}
                        statusFilter={statusFilter}
                        onStatusChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
                    />

                    <OrdersTable
                        orders={orders}
                        totalOrders={totalOrders}
                        searchQuery={searchQuery}
                        selectedOrders={selectedOrders}
                        onToggleOrder={toggleOrder}
                        onToggleAll={toggleAll}
                        onStatusChange={handleStatusChange}
                        onDownloadPdf={downloadOrderPdf}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        setCurrentPage={setCurrentPage}
                    />
                </div>
            </div>

            <EmailPreparationModal
                open={showEmailModal}
                onOpenChange={setShowEmailModal}
                recipient={emailRecipient}
                onRecipientChange={setEmailRecipient}
                sending={emailSending}
                onSend={handleSendEmail}
                stockData={pendingStockData}
            />
        </>
    );
};
